import { useCallback } from 'react';

import { useLocalStorage } from 'hooks/useLocalStorage';

const FAVORITES_KEY = 'userLibraryFavorites';
const FLAGGED_KEY = 'userLibraryFlagged';
const POSITIVE_KEY = 'userLibraryPositive';
const NEGATIVE_KEY = 'userLibraryNegative';
const FOOT_CONTACTS_KEY = 'userLibraryFootContacts';

// Helper function to convert between Set and Array for localStorage
const setToArray = (set: Set<string>) => Array.from(set);
const arrayToSet = (array: string[] | null) => new Set(array || []);

export function useLibraryStore() {
  const [favoritesArray, setFavoritesArray] = useLocalStorage<string[]>(
    FAVORITES_KEY,
    [],
  );
  const [flaggedArray, setFlaggedArray] = useLocalStorage<string[]>(
    FLAGGED_KEY,
    [],
  );
  const [positivesArray, setPositivesArray] = useLocalStorage<string[]>(
    POSITIVE_KEY,
    [],
  );
  const [negativesArray, setNegativesArray] = useLocalStorage<string[]>(
    NEGATIVE_KEY,
    [],
  );
  const [footContactsArray, setFootContactsArray] = useLocalStorage<string[]>(
    FOOT_CONTACTS_KEY,
    [],
  );

  // Convert arrays to Sets for use in the component
  const favorites = arrayToSet(favoritesArray);
  const flagged = arrayToSet(flaggedArray);
  const positives = arrayToSet(positivesArray);
  const negatives = arrayToSet(negativesArray);
  const footContacts = arrayToSet(footContactsArray);

  // Helper to create a new Set with the updated value
  const updateSet = useCallback(
    (set: Set<string>, value: string, shouldAdd: boolean) => {
      const newSet = new Set(set);
      if (shouldAdd) {
        newSet.add(value);
      } else {
        newSet.delete(value);
      }
      return newSet;
    },
    [],
  );

  const addToFavorites = useCallback(
    (jobID: string) => {
      setFavoritesArray(setToArray(updateSet(favorites, jobID, true)));
    },
    [favorites, setFavoritesArray, updateSet],
  );

  const removeFromFavorites = useCallback(
    (jobID: string) => {
      setFavoritesArray(setToArray(updateSet(favorites, jobID, false)));
    },
    [favorites, setFavoritesArray, updateSet],
  );

  const addToFlagged = useCallback(
    (jobID: string) => {
      setFlaggedArray(setToArray(updateSet(flagged, jobID, true)));
    },
    [flagged, setFlaggedArray, updateSet],
  );

  const addPositiveFeedback = useCallback(
    (jobID: string) => {
      setPositivesArray(setToArray(updateSet(positives, jobID, true)));
      // Remove from negative if it exists
      setNegativesArray(setToArray(updateSet(negatives, jobID, false)));
    },
    [positives, negatives, setPositivesArray, setNegativesArray, updateSet],
  );

  const addNegativeFeedback = useCallback(
    (jobID: string) => {
      setNegativesArray(setToArray(updateSet(negatives, jobID, true)));
      // Remove from positive if it exists
      setPositivesArray(setToArray(updateSet(positives, jobID, false)));
    },
    [positives, negatives, setPositivesArray, setNegativesArray, updateSet],
  );

  const addFootContacts = useCallback(
    (jobID: string) => {
      setFootContactsArray(setToArray(updateSet(footContacts, jobID, true)));
    },
    [footContacts, setFootContactsArray, updateSet],
  );

  const isBrowseFavorite = useCallback(
    (jobID: string) => {
      return favorites.has(jobID);
    },
    [favorites],
  );

  const isFlagged = useCallback(
    (jobID: string) => {
      return flagged.has(jobID);
    },
    [flagged],
  );

  const hasPositiveFeedback = useCallback(
    (jobID: string) => {
      return positives.has(jobID);
    },
    [positives],
  );

  const hasNegativeFeedback = useCallback(
    (jobID: string) => {
      return negatives.has(jobID);
    },
    [negatives],
  );

  const hasFootContacts = useCallback(
    (jobID: string) => {
      return footContacts.has(jobID);
    },
    [footContacts],
  );

  return {
    favorites,
    flagged,
    positives,
    negatives,
    footContacts,
    addToFavorites,
    removeFromFavorites,
    addToFlagged,
    addPositiveFeedback,
    addNegativeFeedback,
    addFootContacts,
    isBrowseFavorite,
    isFlagged,
    hasPositiveFeedback,
    hasNegativeFeedback,
    hasFootContacts,
  };
}
